<template>
    <div ref="root" class="nested-dropdown" @click.stop>
        <!-- Кнопка‑триггер -->
        <button
            type="button"
            class="dropdown-toggle"
            @click="toggle"
            :aria-expanded="open.toString()"
            aria-haspopup="true"
        >
            <span>{{ selected?.[labelKey] ?? placeholder }}</span>
            <span class="material-icons arrow">arrow_drop_down</span>
        </button>

        <!-- Основной список -->
        <ul v-if="open" class="dropdown-menu" role="menu">
            <li
                v-for="(item, idx) in items"
                :key="idx"
                class="dropdown-item"
                :class="{ hovered: hoveredIdx === idx }"
                @mouseenter="handleMouseEnter(idx, item)"
                @mouseleave="handleMouseLeave(idx, item)"
            >
                <!-- Сам элемент -->
                <div
                    class="item-label"
                    :class="{ disabled: item.disabled }"
                    @click="!item.menu && !item.disabled && onSelect(item)"
                >
                    {{ item[labelKey] }}
                    <span v-if="item.menu" class="material-icons caret" aria-hidden="true">
            chevron_right
          </span>
                </div>

                <!-- Вложенный список (если есть) -->
                <ul
                    v-show="item.menu && openState[idx]"
                    class="submenu"
                    role="menu"
                >
                    <li
                        v-for="(sub, subIdx) in item.menu"
                        :key="subIdx"
                        :class="{ disabledItem: sub.disabled }"
                        @click.stop="!sub.disabled && selectSub(sub, idx)"
                    >
                        {{ sub[labelKey] }}
                    </li>
                </ul>
            </li>
        </ul>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount, watch } from 'vue';

const props = defineProps({
    items: { type: Array, required: true },
    labelKey: { type: String, default: 'label' },
    valueKey: { type: String, default: 'value' },
    placeholder: { type: String, default: 'Выберите…' }
});
const emit = defineEmits(['select']);

const { items, labelKey, valueKey, placeholder } = props;

const root = ref(null);
const open = ref(false);
const selected = ref(null);
const openState = reactive({});
const hoveredIdx = ref(null);

/* ---------- сброс открытых под‑меню ---------- */
function resetOpenState() {
    Object.keys(openState).forEach(k => delete openState[k]);
}

/* ---------- открываем/закрываем основной список ---------- */
function toggle() {
    if (!open.value) resetOpenState(); // если открываем: очищаем
    open.value = !open.value;
    if (!open.value) resetOpenState(); // если закрываем: также очищаем
}

/* ---------- выбор элемента ---------- */
function onSelect(item) {
    selected.value = item;
    emit('select', item[valueKey]);
    open.value = false;
    hoveredIdx.value = null;
    resetOpenState();
}

/* ---------- подменю открытие / закрытие ----------
   - только если пункт не отключён
*/
function handleMouseEnter(idx, item) {
    if (!item.disabled) openState[idx] = true;
    hoveredIdx.value = idx;
}
function handleMouseLeave(idx, item) {
    if (!item.disabled) openState[idx] = false;
    hoveredIdx.value = null;
}

/* ---------- под‑меню: выбор ---------- */
function selectSub(sub, parentIdx) {
    onSelect(sub);
    hoveredIdx.value = parentIdx;
}

/* ---------- клик вне выпадающего списка ---------- */
function handleClickOutside(e) {
    if (root.value && !root.value.contains(e.target)) {
        open.value = false;
        resetOpenState();
    }
}
onMounted(() => document.addEventListener('click', handleClickOutside));
onBeforeUnmount(() => document.removeEventListener('click', handleClickOutside));

/* ---------- сброс открытых пунктов при смене данных ---------- */
watch(
    () => items,
    () => Object.keys(openState).forEach(k => delete openState[k])
);
</script>

<style scoped>
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');

/* ---------- Основные стили ---------- */
.nested-dropdown {
    position: relative;
    display: inline-block;
    font-family: Arial, sans-serif;
    user-select: none;
    width: 100%; 
    max-width: 360px;
    height: 50px;
}
.dropdown-toggle {
    padding: 8px 12px;
    font-size: 1rem;
    cursor: pointer;
    border: 2px solid #FF8818;
    border-radius: 0.5rem;
    background: #fff;
    text-align: left;
    min-width: 0; 
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.arrow { font-size: 18px; }

.dropdown-menu,
.submenu {
    position: absolute;
    top: 100%;
    left: 0;
    margin: 0;
    padding: 4px 0;
    list-style: none;
    background: #fff;
    border: 2px solid #FF8818;
    border-radius: 0.5rem;
    min-width: 0; 
    width: 100%;
    z-index: 1000;
}
.submenu {
    left: 100%;
    top: 0;
    min-width: 160px;
    z-index: 2000;   /* чтобы не перекрывать родитель */
}

.dropdown-item { position: relative; }
.item-label {
    padding: 6px 12px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

/* --- стили подменю (тоже такие же, как и item-label) --- */
.submenu li {
    padding: 6px 12px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

/* --- hover для обычных пунктов --- */
.item-label:hover,
.submenu li:hover,
.submenu li:focus {
    background: #7DD3FC;
    color: #ffffff;
}

/* --- disabled у главных пунктов (не меняем стиль) --- */
.item-label.disabled {
    cursor: default;
    color: inherit; /* остатковый цвет */
}

/* --- disabled у под‑меню: серый + курсив + без эффекта hover --- */
.submenu li.disabledItem {
    color: #777;
    font-style: italic;
    cursor: default;
}
.submenu li.disabledItem:hover {
    background: none;
    color: #777;
    cursor: default;
}

/* --- подсветка родителя после выбора из под‑меню --- */
.dropdown-item.hovered > .item-label {
    background: #7DD3FC;
    color: #ffffff;
}

.caret { font-size: 18px; }

@media (max-width: 768px) {


  /* подчёркиваем подсказку, чтобы было ясно, что меню открыто */
  .dropdown-item:not(.disabled) .item-label:hover,
  .active-menu .item-label        { background: #e3f2fd; }

  /* подменю «упаковывается» прямо под родителем */
  .submenu {
    left: 0;
    top: 100%;
    /* чтобы подсказка не прятала кнопку, ставим z-index */
    z-index: 2000;
  }

  /* убираем стрелочку‑caret (опционально) – удобно на телефоне */
  .caret { display: none; }
}
</style>
